// TODO(somewhatabstract): These components should be in their own files.
import {t} from "@lingui/core/macro";
import {Trans} from "@lingui/react/macro";
import {css, StyleSheet} from "aphrodite";
import moment from "moment";
import * as React from "react";

import {addStyle, View} from "@khanacademy/wonder-blocks-core";
import {PhosphorIcon} from "@khanacademy/wonder-blocks-icon";
import {Strut} from "@khanacademy/wonder-blocks-layout";
import Link from "@khanacademy/wonder-blocks-link";
import {color, spacing} from "@khanacademy/wonder-blocks-tokens";
import Tooltip from "@khanacademy/wonder-blocks-tooltip";
import {
    LabelMedium,
    LabelSmall,
    LabelXSmall,
} from "@khanacademy/wonder-blocks-typography";
import type {Assignment} from "@khan/assignments-types";
import {
    AI_GUIDE_ACTIVITY_CONTENT_KIND,
    formatDate,
    getActivityAssignmentUrl,
    getContent,
    isWritingCoachAssignment,
} from "@khan/assignments-utils";
import {doNotTranslate} from "@khan/i18n";
import {mediaQueries} from "@khan/shared-styles";

import {buildURLForAssignment} from "#class-package/assignments/assignment-url-utils.ts";

import infoIcon from "@phosphor-icons/core/bold/info-bold.svg";

const getContentKind = () => ({
    AIGuideActivity: t`Khanmigo Activity`,
    Article: t`Article`,
    Challenge: t`Challenge`,
    CourseChallenge: t`Course Challenge`,
    Exercise: t`Exercise`,
    Interactive: t`Interactive`,
    Project: t`Project`,
    Scratchpad: t`Scratchpad`,
    Talkthrough: t`Talkthrough`,
    TopicQuiz: t`Quiz`,
    TopicUnitTest: t`Unit Test`,
    Video: t`Video`,
});

export class ContentCell extends React.Component<{
    assignment: Assignment;
    testId?: string;
    // This will override the link props for Link
    linkProps?: any;
    // Hide the "Same/Different questions"
    hideQuestionSet?: boolean;
}> {
    render(): React.ReactNode {
        const {assignment, testId, hideQuestionSet} = this.props;
        const {title, kind, defaultUrlPath} = getContent(assignment);

        if (!kind) {
            return (
                <div
                    className={css(styles.contentWrapper)}
                    data-testid={testId}
                >
                    <div className={css(styles.contentDetails)}>
                        <div className={css(styles.contentTitle)}>{title}</div>
                    </div>
                </div>
            );
        }

        const isAIGuideActivityAssignment =
            kind === AI_GUIDE_ACTIVITY_CONTENT_KIND;

        // Build URL based on the assignment's course context to ensure teachers
        // navigate to the content in the course where it was assigned
        const contentUrl = buildURLForAssignment(assignment) ?? defaultUrlPath;

        const href = isAIGuideActivityAssignment
            ? getActivityAssignmentUrl(assignment)
            : contentUrl;

        const linkProps = this.props.linkProps || {
            href,
            target: "_blank",
        };

        const {exerciseConfig} = assignment;
        const sameQuestions =
            exerciseConfig && exerciseConfig.itemPickerStrategy === "SAME";
        const separator = doNotTranslate` • `;
        const questionSetText = sameQuestions
            ? t`${separator} Same question set`
            : t`${separator} Different question set`;

        const updatedTitle =
            isAIGuideActivityAssignment && assignment.title
                ? assignment.title
                : title;

        const label = (
            <LabelSmall style={styles.truncatedText} title={updatedTitle}>
                {updatedTitle}
            </LabelSmall>
        );
        const contentKind = getContentKind();

        const subtitle =
            isAIGuideActivityAssignment && assignment.title
                ? `${title}${separator}${contentKind[kind]}`
                : !hideQuestionSet && kind === "Exercise"
                  ? `${contentKind[kind]}${questionSetText}`
                  : contentKind[kind];

        const instructionsText = t`Instructions`;

        return (
            <View testId={testId} style={styles.contentDetails}>
                {isWritingCoachAssignment(assignment) ? (
                    label
                ) : (
                    <Link {...linkProps}>{label}</Link>
                )}
                <LabelXSmall style={styles.hiddenOnMobile}>
                    {subtitle}
                </LabelXSmall>
                {isAIGuideActivityAssignment && assignment.instructions && (
                    <Tooltip
                        content={assignment.instructions}
                        placement="bottom"
                    >
                        <View style={styles.instructions}>
                            <View style={styles.instructionsWithIcon}>
                                <PhosphorIcon
                                    aria-label={assignment.instructions}
                                    icon={infoIcon}
                                />
                                <Strut size={spacing.xxxxSmall_2} />
                                <LabelXSmall>{instructionsText}</LabelXSmall>
                            </View>
                        </View>
                    </Tooltip>
                )}
            </View>
        );
    }
}

export class DateTimeCell extends React.Component<{
    // The dateTime to display in a human readable way.
    dateTime: Date | string;
    // Whether to display only the date portion of the dateTime.
    showDateOnly?: boolean;
    // Whether to format as a "late" assignment.
    // Used for students who have not yet submitted assignments that are
    // overdue.
    isLate?: boolean;
    // Whether the assignment is past due.
    isPastDueDate?: boolean;
    // Whether we want to add a Tooltip + Icon in the date cell.
    description?: React.ReactNode;
}> {
    static defaultProps: {
        showDateOnly: boolean;
    } = {
        showDateOnly: false,
    };

    render(): React.ReactNode {
        const {dateTime, description, showDateOnly, isLate, isPastDueDate} =
            this.props;

        // If the date specified is within the next week, show a friendly
        // string ("Friday, 2PM") instead of the full date.
        const useSimpleDatetime = moment(dateTime).isBetween(
            moment(),
            moment().add(6, "days"),
        );

        const formattedDateTime = formatDate(
            dateTime,
            showDateOnly,
            useSimpleDatetime,
        );

        const cellContents = isLate ? (
            <Trans>Late: {formattedDateTime}</Trans>
        ) : (
            formattedDateTime
        );

        return (
            <div
                className={css(
                    styles.dateTime,
                    isLate && styles.isLate,
                    isPastDueDate && styles.isPastDueDate,
                )}
            >
                {description}
                {cellContents}
            </div>
        );
    }
}

/**
 * Renders the assignment name and kind as plain text (no links included).
 */
export class ContentOnlyCell extends React.Component<{
    assignment: Assignment;
    testId?: string;
}> {
    render(): React.ReactNode {
        const {assignment, testId} = this.props;
        const {title, kind} = getContent(assignment);

        const {exerciseConfig} = assignment;
        const sameQuestions =
            exerciseConfig && exerciseConfig.itemPickerStrategy === "SAME";
        const separator = doNotTranslate` • `;
        const questionSetText = sameQuestions
            ? t`${separator} Same question set`
            : t`${separator} Different question set`;
        const contentKind = getContentKind();

        return (
            <View testId={testId} style={styles.contentDetails}>
                <LabelSmall style={styles.truncatedText} title={title}>
                    {title}
                </LabelSmall>
                {kind && (
                    <LabelXSmall style={styles.hiddenOnMobile}>
                        {contentKind[kind]}
                        {kind === "Exercise" && questionSetText}
                    </LabelXSmall>
                )}
            </View>
        );
    }
}

const CIRCLE_RADIUS = 4;
const SVG_SIZE = CIRCLE_RADIUS * CIRCLE_RADIUS;
const CIRCLE_POSITION = SVG_SIZE * 0.5;

// @ts-expect-error - TS2558 - Expected 2 type arguments, but got 1.
const StyledSvg = addStyle<"svg">("svg", {
    marginRight: -spacing.medium_16,
    marginBottom: -CIRCLE_RADIUS * 0.5,
});

type DateTimeDiffCellProps = {
    /**
     * The original text.
     */
    originalText: string;
    /**
     * The text that will be used to compare against the original one.
     */
    textToCompare?: string | null | undefined;
};

/**
 * Renders the difference (if exists) between two texts. If both texts are the
 * same or the second is null, then we'll only display the original text.
 */
export class TextDiffCell extends React.Component<DateTimeDiffCellProps> {
    renderCircle(): React.ReactNode {
        return (
            // @ts-expect-error - TS2322 - Type '{ children: Element; width: number; height: number; viewBox: string; "aria-hidden": boolean; }' is not assignable to type 'PropsWithoutRef<{ className?: string | undefined; style?: StyleType; children?: ReactNode; } & Omit<ComponentProps<T>, "style">>'.
            <StyledSvg
                width={SVG_SIZE}
                height={SVG_SIZE}
                viewBox={`0 0 ${SVG_SIZE} ${SVG_SIZE}`}
                aria-hidden={true}
            >
                <circle
                    cx={CIRCLE_POSITION}
                    cy={CIRCLE_POSITION}
                    r={CIRCLE_RADIUS}
                    fill={color.blue}
                />
            </StyledSvg>
        );
    }

    renderPrimaryText(isDiff: boolean): React.ReactNode {
        const {originalText, textToCompare} = this.props;

        if (!isDiff) {
            return (
                <LabelMedium style={styles.textDiffUnchanged}>
                    {originalText}
                </LabelMedium>
            );
        }

        return (
            <LabelMedium style={styles.textDiffPrimary} tag="ins">
                {textToCompare}
                {this.renderCircle()}
            </LabelMedium>
        );
    }

    /**
     * Optionally renders a secondary row including the original text with a
     * different format.
     * @param {boolean} isDiff Whether both texts are the same
     */
    maybeRenderSecondaryText(isDiff: boolean): React.ReactNode {
        if (!isDiff) {
            return null;
        }

        return (
            <LabelMedium style={styles.textDiffSecondary} tag="del">
                {this.props.originalText}
            </LabelMedium>
        );
    }

    render(): React.ReactNode {
        const {originalText, textToCompare} = this.props;

        const isDiff = textToCompare
            ? !!(textToCompare !== originalText)
            : false;

        return (
            <View style={styles.textDiffWrapper}>
                {this.renderPrimaryText(isDiff)}
                {this.maybeRenderSecondaryText(isDiff)}
            </View>
        );
    }
}

const styles = StyleSheet.create({
    contentWrapper: {
        display: "flex",
        alignItems: "center",
    },
    contentDetails: {
        display: "inline-block",
        fontWeight: 600,
        width: 320,
        [mediaQueries.xsOrSmaller]: {
            width: 120,
            marginLeft: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
        },
    },
    contentTitle: {
        fontSize: 15,
        color: color.offBlack,
        marginBottom: 2,
    },
    dateTime: {
        width: 150,
        color: color.offBlack,
        whiteSpace: "nowrap",
    },
    instructions: {
        float: "left",
        overflow: "auto",
    },
    instructionsWithIcon: {
        flexDirection: "row",
    },
    isLate: {
        color: color.red,
    },
    hiddenOnMobile: {
        [mediaQueries.xsOrSmaller]: {
            display: "none",
        },
    },
    truncatedText: {
        fontWeight: 700,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        width: "100%",
    },
    isPastDueDate: {
        fontStyle: "italic",
        color: color.offBlack64,
    },
    textDiffWrapper: {
        width: 120,
    },
    textDiffUnchanged: {
        lineHeight: "2.5em",
    },
    textDiffPrimary: {
        fontWeight: "bold",
        textDecoration: "none",
    },
    textDiffSecondary: {
        color: color.offBlack32,
    },
});
